import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { PriorityFilterStatus } from "@/enum/list.enum";
import { ProspectInterest } from "@/enum/prospect.enum";

const PRIORITY_PARAM = "priorityFilter";
const SENTIMENT_PARAM = "sentiment";

export interface InboxQuickFilters {
	/** Lead-priority quick filter ("All qualified leads" etc.); `[ALL]` means no narrowing. */
	priorityFilterStatus: PriorityFilterStatus[];
	setPriorityFilterStatus: (value: PriorityFilterStatus[]) => void;
	/** Reply-sentiment quick filter (Interested / Not interested / …); `[]` means no narrowing. */
	sentimentFilter: ProspectInterest[];
	setSentimentFilter: (value: ProspectInterest[]) => void;
}

const parseEnumArray = <T extends string>(raw: string | null, allowed: T[], fallback: T[]): T[] => {
	if (!raw) {
		return fallback;
	}

	try {
		const parsed = JSON.parse(raw) as unknown;

		if (!Array.isArray(parsed)) {
			return fallback;
		}

		const valid = parsed.filter((v): v is T => allowed.includes(v as T));

		return valid.length > 0 ? valid : fallback;
	} catch {
		return fallback;
	}
};

/**
 * Inbox L3-nav quick filters (PREC-3599): the "qualified leads" (lead-priority) and reply-sentiment
 * selections. The URL is the source of truth — mirrored via `useSearchParams` so the selection
 * persists across reloads and is shareable, the same way the Inbox sort does (see useSortUrlSync).
 * The `ALL` priority / empty sentiment states clear their param (= no narrowing).
 */
export const useInboxQuickFilters = (): InboxQuickFilters => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Key the memos on the raw param *values*, never on `searchParams` itself: react-router hands
	// out a new URLSearchParams instance on every query-string change, so memoising on the object
	// re-creates both arrays whenever *any* unrelated param moves — including `detailTab`, which is
	// the lead panel's own tab state. The Inbox fetch effect lists both arrays in its dependencies,
	// so that identity churn cleared the inbox, reset the selected lead and refetched the list on
	// every detail-tab click (it read as a full page reload).
	const priorityParam = searchParams.get(PRIORITY_PARAM);
	const sentimentParam = searchParams.get(SENTIMENT_PARAM);

	const priorityFilterStatus = useMemo(
		() =>
			parseEnumArray<PriorityFilterStatus>(priorityParam, Object.values(PriorityFilterStatus), [
				PriorityFilterStatus.ALL,
			]),
		[priorityParam]
	);

	const sentimentFilter = useMemo(
		() => parseEnumArray<ProspectInterest>(sentimentParam, Object.values(ProspectInterest), []),
		[sentimentParam]
	);

	const setPriorityFilterStatus = useCallback(
		(value: PriorityFilterStatus[]) => {
			setSearchParams(
				(prev) => {
					if (value.length > 0 && !value.includes(PriorityFilterStatus.ALL)) {
						prev.set(PRIORITY_PARAM, JSON.stringify(value));
					} else {
						prev.delete(PRIORITY_PARAM);
					}

					return prev;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const setSentimentFilter = useCallback(
		(value: ProspectInterest[]) => {
			setSearchParams(
				(prev) => {
					if (value.length > 0) {
						prev.set(SENTIMENT_PARAM, JSON.stringify(value));
					} else {
						prev.delete(SENTIMENT_PARAM);
					}

					return prev;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	return { priorityFilterStatus, setPriorityFilterStatus, sentimentFilter, setSentimentFilter };
};
